import React, { useState, useEffect, useMemo } from "react";
import GeoBasedIncrementility from "../../components/GeoBasedIncrementility";
import axiosInstance from "../../utils/axiosInstance";
import MMMDashboardCard from "../../components/MMMDashboardCard";
import { Alert, Spin, Typography, Card } from "antd";
import BidDashboardCard from "../../components/BidDashboardCard";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { usePermissions } from "../../hooks/usePermissions";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
interface GeoLiftOutput {
  outputData: {
    aggregateLift: string; // "4.80" (if it will always be a string, keep string. If numeric, use number)
    aggregateLiftLowerBound: string;
    aggregateLiftUpperBound: string;
    aggregateROASLowerBound: string;
    aggregateROASUpperBound: string;
    outputWithoutCampaign: number;
    totalAdSpend: number;
    outputWithCampaign: number;
  };
}
interface GeoTest {
  title: string;
  subtitle?: string;
  date: string;
  action?: "VIEW_RESULTS" | "VIEW_CONTROLS";
  jobConfigId: string;
  name: string;
  buyType: string;
  jobType: string;
  description: string | null;
  createdDate: string;
  modifiedDate: string;
  version: number;
  dataMappingConfig: {
    mappings: {
      mmm: {
        columnMapping: {
          media_variables: string[];
          impression_variables: string[];
          target_variables: string[];
          non_media_variables: string[];
          date_variables: string[];
        };
      };
    };
  };
  modelOutputConfig: GeoLiftOutput;
}

interface SalesData {
  withoutCampaign: number;
  withCampaign: number;
  adSpend: number;
  incrementalSales: number;
  totalLiftPercentage: number;
  totalLiftPercentageLowerBound: number;
  totalLiftPercentageUpperBound: number;
  totalROASLowerBound: number;
  totalROASUpperBound: number;
  jobConfigId: any;
}

interface JobConfig {
  jobConfigId: string;
  name: string;
  jobType: string;
  description: string | null;
  createdDate: string;
  modifiedDate: string;
  version: number;
  modelOutputConfig?: { outputData?: any };
}

interface ApiResponse {
  items: JobConfig[];
  total_count: number;
  limit: number;
  offset: number;
}

interface JobRun {
  jobRunId: string;
  tenantId: string;
  jobConfigId: string;
  jobType: string;
  jobStatus: string;
  jobRunCount: number;
  jobRunTimeout: number;
  jobExecutionTime: string;
  createdDate: string;
  modifiedDate: string;
}

interface JobRunApiResponse {
  items: JobRun[];
  total_count: number;
  limit: number;
  offset: number;
}
interface MMMJobConfig {
  jobConfigId: string;
  name: string;
  jobType: string;
  description: string | null;
  createdDate: string;
  modifiedDate: string;
  version: number;
  dataMappingConfig: {
    mappings: {
      mmm: {
        columnMapping: {
          media_variables: string[];
          impression_variables: string[];
          target_variables: string[];
          non_media_variables: string[];
          date_variables: string[];
        };
      };
    };
  };
  modelOutputConfig?: { outputData?: any };
}

interface MMMApiResponse {
  items: MMMJobConfig[];
  total_count: number;
  limit: number;
  offset: number;
}

const Dashboard: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [geoTests, setGeoTests] = useState<GeoTest[]>([]);
  const [jobRuns, setJobRuns] = useState<JobRun[]>([]);
  const [mmmJobRuns, setMmmJobRuns] = useState<JobRun[]>([]);
  const [selectedJobIndex, setSelectedJobIndex] = useState<number>(0);
  const [selectedMmmJobConfigId, setSelectedMmmJobConfigId] = useState<string | null>(null);
  const [selectedBidJobConfigId, setSelectedBidJobConfigId] = useState<string | null>(null);
  // no async cache needed — MMM/Bid output is embedded in modelOutputConfig (like geo)
  const [bidJobRuns, setBidJobRuns] = useState<JobRun[]>([]);
  const [isGeoDataLoading, setIsGeoDataLoading] = useState(true);
  const [isMmmDataLoading, setIsMmmDataLoading] = useState(true);
  const [isBidDataLoading, setIsBidDataLoading] = useState(true);
  const [geoDataError, setGeoDataError] = useState<string | null>(null);
  const [mmmDataError, setMmmDataError] = useState<string | null>(null);
  const [bidDataError, setBidDataError] = useState<string | null>(null);
  const [mmmData, setMmmData] = useState<MMMJobConfig[]>([]);
  const [bidData, setBidData] = useState<JobConfig[]>([]);

  const formatDate = (dateString: string) => {
    return dayjs
      .utc(`${dateString}Z`)
      .local()
      .format("DD MMM | hh:mm A");
  };

  // Helper function to check if a job is stuck (same logic as synthetic control list)
  const getActualJobStatus = (jobRun: JobRun | undefined): string => {
    if (!jobRun) return "UNKNOWN";

    const { jobStatus, jobExecutionTime } = jobRun;
    const isStuck =
      (jobStatus === "RUNNING" || jobStatus === "PENDING") &&
      dayjs.utc().diff(dayjs.utc(jobExecutionTime), "hour") >= 2;

    return isStuck ? "FAILED" : jobStatus;
  };

  // Auto-select first MMM test that has embedded output data
  useEffect(() => {
    if (mmmData.length === 0 || selectedMmmJobConfigId) return;
    const first = mmmData.find((t) => !!t.modelOutputConfig?.outputData);
    if (first) setSelectedMmmJobConfigId(first.jobConfigId);
  }, [mmmData]);

  // Auto-select first Bid Optimizer test that has embedded output data
  useEffect(() => {
    if (bidData.length === 0 || selectedBidJobConfigId) return;
    const first = bidData.find((t) => !!t.modelOutputConfig?.outputData);
    if (first) setSelectedBidJobConfigId(first.jobConfigId);
  }, [bidData]);

  useEffect(() => {
    if (geoTests.length === 0) return;
    const firstValidIndex = geoTests.findIndex((test) => {
      const jobRun = jobRuns.find((run) => run.jobConfigId === test.jobConfigId);
      return getActualJobStatus(jobRun) === "SUCCESSFUL" && !!test.modelOutputConfig?.outputData;
    });
    if (firstValidIndex !== -1) {
      setSelectedJobIndex(firstValidIndex);
    }
  }, [geoTests, jobRuns]);
  const salesData = useMemo(() => {
    const filteredGeoLift = geoTests.filter(
      (ele) => ele.jobType === "GEO_LIFT" && ele.buyType === "national"
    );

    const selectedGeoTest = geoTests[selectedJobIndex];
    const selectedGeoLiftTest = selectedGeoTest
      ? filteredGeoLift.find((t) => t.jobConfigId === selectedGeoTest.jobConfigId)
      : undefined;

    if (!selectedGeoLiftTest?.modelOutputConfig?.outputData) return undefined;

    const outputData = selectedGeoLiftTest.modelOutputConfig.outputData;

    const transformedData: SalesData = {
      withoutCampaign: Number(
        Math.round(outputData.outputWithoutCampaign || 0).toFixed(2)
      ),
      withCampaign: Number(
        Math.round(outputData.outputWithCampaign || 0).toFixed(2)
      ),
      adSpend: Number((outputData.totalAdSpend || 0).toFixed(2)),
      incrementalSales: Number(
        (outputData.outputWithCampaign || 0) -
          (outputData.outputWithoutCampaign || 0)
      ),
      jobConfigId: selectedGeoLiftTest.jobConfigId,
      totalLiftPercentage: Number(outputData.aggregateLift || 0),
      totalLiftPercentageLowerBound: Number(outputData.aggregateLiftLowerBound || 0),
      totalLiftPercentageUpperBound: Number(outputData.aggregateLiftUpperBound || 0),
      totalROASLowerBound: Number(outputData.aggregateROASLowerBound || 0),
      totalROASUpperBound: Number(outputData.aggregateROASUpperBound || 0),
    };

    return transformedData;
  }, [geoTests, selectedJobIndex]);

  const handleMmmJobSelect = (jobConfigId: string): void => {
    setSelectedMmmJobConfigId(jobConfigId);
  };

  // Derive MMM output directly from embedded modelOutputConfig — no API call needed
  const mmmDataOfRoi = useMemo(() => {
    if (!selectedMmmJobConfigId) return null;
    const selected = mmmData.find((t) => t.jobConfigId === selectedMmmJobConfigId);
    return selected?.modelOutputConfig?.outputData ?? null;
  }, [mmmData, selectedMmmJobConfigId]);

  const bidOutputData = useMemo(() => {
    if (!selectedBidJobConfigId) return null;
    const selected = bidData.find((t) => t.jobConfigId === selectedBidJobConfigId);
    return selected?.modelOutputConfig?.outputData ?? null;
  }, [bidData, selectedBidJobConfigId]);

  useEffect(() => {
    const fetchAllData = async () => {
      // Only fetch data for modules the tenant has permission to access
      const shouldFetchGeo = hasPermission("measurement");
      const shouldFetchMMM = hasPermission("planning");
      const shouldFetchBid = hasPermission("buying");

      if (shouldFetchGeo) {
        setIsGeoDataLoading(true);
      }
      if (shouldFetchMMM) {
        setIsMmmDataLoading(true);
      }
      if (shouldFetchBid) {
        setIsBidDataLoading(true);
      }

      try {
        // Fetch Geo/Measurement data only if tenant has permission
        if (shouldFetchGeo) {
          try {
            const [geoDataResponse, jobRunsResponse] = await Promise.all([
              axiosInstance("/api/jobConfig/jobType/GEO_LIFT"),
              axiosInstance("/api/jobRun/jobType/GEO_LIFT"),
            ]);
            // Process GeoTest data
            const geoData: ApiResponse = geoDataResponse.data;
            const transformedTests: GeoTest[] = geoData.items.map(
              (item: any) => ({
                title: item.name,
                subtitle: item.description || undefined,
                date: formatDate(item.createdDate),
                action: "VIEW_RESULTS",
                jobConfigId: item.jobConfigId,
                buyType: item?.modelParameterConfig?.parameters?.buyType,
                isSales: item?.modelParameterConfig?.parameters?.isSales,
                ...item,
              })
            );
            setGeoTests(
              transformedTests.sort(
                (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
              )
            );

            const jobRunsData: JobRunApiResponse = jobRunsResponse.data;
            setJobRuns(jobRunsData.items);
            setGeoDataError(null);
          } catch (err) {
            setGeoDataError(
              err instanceof Error ? err.message : "Failed to load Geo data"
            );
          } finally {
            setIsGeoDataLoading(false);
          }
        } else {
          setIsGeoDataLoading(false);
        }

        // Fetch MMM data only if tenant has permission
        if (shouldFetchMMM) {
          try {
            setIsMmmDataLoading(true);
            const [mmmDataResponse, mmmJobRunsResponse] = await Promise.all([
              axiosInstance("/api/jobConfig/jobType/MMM"),
              axiosInstance("/api/jobRun/jobType/MMM"),
            ]);

            // Process MMM data
            const mmmData: MMMApiResponse = mmmDataResponse.data;
            setMmmData(
              [...mmmData.items].sort(
                (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
              )
            );

            // Process MMM JobRuns data
            const mmmJobRunsData: JobRunApiResponse = mmmJobRunsResponse.data;
            setMmmJobRuns(mmmJobRunsData.items);
            setMmmDataError(null);
          } catch (err) {
            setMmmDataError(
              err instanceof Error ? err.message : "Failed to load MMM data"
            );
            console.error("Error fetching MMM data:", err);
          } finally {
            setIsMmmDataLoading(false);
          }
        } else {
          setIsMmmDataLoading(false);
        }

        // Fetch Bid Optimizer data only if tenant has permission
        if (shouldFetchBid) {
          try {
            setIsBidDataLoading(true);

            const [bidDataResponse, bidJobRunsResponse] = await Promise.all([
              axiosInstance("/api/jobConfig/jobType/BidOptimizer"),
              axiosInstance("/api/jobRun/jobType/BidOptimizer"),
            ]);
            const bidData: ApiResponse = bidDataResponse.data;
            setBidData(
              [...bidData.items].sort(
                (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
              )
            );
            const bidJobRunsData: JobRunApiResponse = bidJobRunsResponse.data;
            setBidJobRuns(bidJobRunsData.items);
            setBidDataError(null);
          } catch (err) {
            setBidDataError(
              err instanceof Error
                ? err.message
                : "Failed to load Bid Optimizer data"
            );
            console.error("Error fetching Bid Optimizer data:", err);
          } finally {
            setIsBidDataLoading(false);
          }
        } else {
          setIsBidDataLoading(false);
        }
      } catch (err) {
        console.error("Error in fetchAllData:", err);
      }
    };
    fetchAllData();
  }, [hasPermission]);

  return (
    <div className="dashboard-container p-4">

      {/* Measurement/Geo-Lift Section - Always show if user has measurement permission */}
      {hasPermission("measurement") && (
        <>
          {isGeoDataLoading && (
            <Card className="shadow-sm">
              <div className="flex justify-center items-center p-8">
                <Spin size="large" />
                <Text className="ml-3 text-gray-600">
                  Loading Incrementality data...
                </Text>
              </div>
            </Card>
          )}
          {!isGeoDataLoading && (
            <div className="mb-5 rounded-lg">
              <GeoBasedIncrementility
                geoTests={geoTests}
                salesData={salesData}
                totalLiftPercentage={salesData?.totalLiftPercentage}
                totalLiftPercentageLowerBound={salesData?.totalLiftPercentageLowerBound}
                totalLiftPercentageUpperBound={salesData?.totalLiftPercentageUpperBound}
                totalROASLowerBound={salesData?.totalROASLowerBound}
                totalROASUpperBound={salesData?.totalROASUpperBound}
                jobRuns={jobRuns}
                setSelectedJobIndex={setSelectedJobIndex}
                selectedJobIndex={selectedJobIndex}
              />
            </div>
          )}
        </>
      )}

      {/* MMM Dashboard Section - Only show if user has planning permission */}
      {hasPermission("planning") && (
        <div className="mb-5">
          {isMmmDataLoading ? (
            <Card className="shadow-sm">
              <div className="flex justify-center items-center p-8">
                <Spin size="large" />
                <Text className="ml-3 text-gray-600">Loading MMM data...</Text>
              </div>
            </Card>
          ) : mmmDataError ? (
            <Card className="shadow-sm">
              <Alert
                message="Error Loading MMM Data"
                description={mmmDataError}
                type="error"
                showIcon
                className="mb-4"
              />
              <div className="flex justify-center">
                <button
                  className="bg-[#0055B8] hover:bg-[#003D82] text-white px-4 py-2 rounded"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            </Card>
          ) : (
            <MMMDashboardCard
              dataset={mmmDataOfRoi}
              selectedJobConfigId={selectedMmmJobConfigId}
              onSelect={handleMmmJobSelect}
              mmmTests={mmmData
                .filter((item) => !!item.modelOutputConfig?.outputData)
                .map((item) => ({
                  name: item.name,
                  date: formatDate(item.createdDate),
                  status: "completed" as const,
                  mediaVariables:
                    item.dataMappingConfig?.mappings?.mmm?.columnMapping
                      ?.media_variables || [],
                  jobConfigId: item.jobConfigId,
                  showControls: true,
                }))}
            />
          )}
        </div>
      )}

      {/* Bid Optimizer Dashboard Section - Only show if user has buying permission */}
      {hasPermission("buying") && (
        <div className="mb-8">
          {isBidDataLoading ? (
            <Card className="shadow-sm">
              <div className="flex justify-center items-center p-8">
                <Spin size="large" />
                <Text className="ml-3 text-gray-600">
                  Loading Bid Optimizer data...
                </Text>
              </div>
            </Card>
          ) : bidDataError ? (
            <Card className="shadow-sm">
              <Alert
                message="Error Loading Bid Optimizer Data"
                description={bidDataError}
                type="error"
                showIcon
                className="mb-4"
              />
              <div className="flex justify-center">
                <button
                  className="bg-[#0055B8] hover:bg-[#003D82] text-white px-4 py-2 rounded"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            </Card>
          ) : (
            <BidDashboardCard
              bidTests={bidData
                .filter((item) => !!item.modelOutputConfig?.outputData)
                .map((item) => ({
                  name: item.name,
                  date: formatDate(item.createdDate),
                  jobConfigId: item.jobConfigId,
                }))}
              selectedJobConfigId={selectedBidJobConfigId}
              onSelect={setSelectedBidJobConfigId}
              bidOutputData={bidOutputData}
            />
          )}
        </div>
      )}

      {/* Show message if user has no permissions */}
      {!hasPermission("measurement") &&
        !hasPermission("planning") &&
        !hasPermission("buying") && (
          <Card className="shadow-sm">
            <Alert
              message="No Access"
              description="You do not have permission to view any dashboard data. Please contact your administrator."
              type="warning"
              showIcon
            />
          </Card>
        )}
    </div>
  );
};

export default Dashboard;
